import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';

import CardShell from '../components/CardShell';
import ClosureEmployeeBalanceModal from '../components/ClosureEmployeeBalanceModal';
import {
  downloadConsolidatedPaymentHistoryPdf,
  downloadEmployeePaymentHistoryPdf,
  getConsolidatedPaymentHistory,
  getEmployeePaymentHistory,
  listPaymentHistoryClosures,
  listPaymentHistoryEmployees,
} from '../api/liquidacionApi';
import { formatMoney } from '../utils/moneyFormat';
import { fmtCompactDateRange } from '../../../../shared/utils/dateFormat';

const EMPLOYEE_GRID_COLS =
  '145px minmax(125px, 1.3fr) 75px 85px 110px 110px 100px 105px 115px 52px';
const CONSOLIDATED_GRID_COLS =
  'minmax(190px, 1.7fr) 105px 70px 85px 115px 105px 110px 125px 52px';

const FILTER_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    bgcolor: 'white',
    '& fieldset': { borderColor: 'neutral.300' },
    '&:hover fieldset': { borderColor: 'primary.400' },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.700',
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputLabel-root': {
    px: 1,
    py: 0.25,
    ml: -0.35,
    mt: -0.15,
    bgcolor: 'secondary.200',
    borderRadius: '10px',
    color: 'primary.900',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '0.2px',
    '&.Mui-focused': { color: 'primary.900' },
  },
};

function unwrap(response) {
  return response && typeof response === 'object' && 'data' in response
    ? response.data
    : response;
}

function formatPeriod(start, end) {
  return fmtCompactDateRange(start, end, ' – ');
}

function formatMinutes(value) {
  const total = Math.max(0, Number(value || 0));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

function ReportTableHeader({ labels, columns }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        columnGap: 1,
        alignItems: 'center',
        bgcolor: 'neutral.600',
        px: 2,
        py: 1.6,
        flexShrink: 0,
      }}
    >
      {labels.map((label, index) => (
        <Typography
          key={`${label}-${index}`}
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: 'logoblanco',
            textAlign: 'center',
          }}
        >
          {label}
        </Typography>
      ))}
    </Box>
  );
}

function ReportTotalsRow({ columns, values }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        columnGap: 1,
        alignItems: 'center',
        flexShrink: 0,
        px: 2,
        py: 1.45,
        bgcolor: 'neutral.600',
        borderTop: '2px solid',
        borderColor: 'neutral.500',
      }}
    >
      {values.map((value, index) => (
        <Typography
          key={index}
          title={String(value)}
          sx={{
            px: 0.5,
            color: 'logoblanco',
            fontSize: 12.5,
            fontWeight: 800,
            textAlign: index === 0 ? 'left' : 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </Typography>
      ))}
    </Box>
  );
}

function PaymentRow({ payment, onViewDetails }) {
  const cells = [
    formatPeriod(payment.periodStart, payment.periodEnd),
    payment.closureName || '—',
    formatMinutes(payment.workedMinutes),
    payment.salaryMode === 'HORA' ? 'Por hora' : payment.salaryMode === 'MENSUAL' ? 'Mensual' : '—',
    payment.salaryMode === 'HORA' && payment.hourlyRate !== null
      ? formatMoney(payment.hourlyRate)
      : '—',
    formatMoney(payment.baseAmount),
    formatMoney(payment.bonusTotal),
    formatMoney(payment.discountTotal),
    formatMoney(payment.totalAmount),
  ];

  return (
    <ReportRow columns={EMPLOYEE_GRID_COLS}>
      {cells.map((cell, index) => (
        <ReportCell
          key={index}
          value={cell}
          align={index === 1 ? 'left' : 'center'}
          strong={index === 8}
          color={index === 6 ? 'success.700' : index === 7 ? 'error.700' : 'primary.900'}
        />
      ))}
      <DetailButton title="Ver bonos y descuentos" onClick={() => onViewDetails(payment)} />
    </ReportRow>
  );
}

function ConsolidatedRow({ employee, onViewEmployee }) {
  const cells = [
    employee.fullName || '—',
    employee.dni || '—',
    employee.paymentCount || 0,
    formatMinutes(employee.workedMinutes),
    formatMoney(employee.baseAmount),
    formatMoney(employee.bonusTotal),
    formatMoney(employee.discountTotal),
    formatMoney(employee.totalAmount),
  ];

  return (
    <ReportRow columns={CONSOLIDATED_GRID_COLS}>
      {cells.map((cell, index) => (
        <ReportCell
          key={index}
          value={cell}
          align={index === 0 ? 'left' : 'center'}
          strong={index === 0 || index === 7}
          color={index === 5 ? 'success.700' : index === 6 ? 'error.700' : 'primary.900'}
        />
      ))}
      <DetailButton title="Ver pagos del empleado" onClick={() => onViewEmployee(employee)} />
    </ReportRow>
  );
}

function ReportRow({ columns, children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        columnGap: 1,
        alignItems: 'center',
        px: 2,
        py: 1.25,
        bgcolor: 'transparent',
        borderBottom: '1px solid rgba(0,0,0,.06)',
        '&:hover': { bgcolor: 'rgba(0,0,0,.03)' },
      }}
    >
      {children}
    </Box>
  );
}

function ReportCell({ value, align = 'center', strong = false, color = 'primary.900' }) {
  return (
    <Typography
      title={String(value)}
      sx={{
        px: 0.5,
        fontSize: 12.5,
        fontWeight: strong ? 700 : 600,
        color,
        textAlign: align,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value}
    </Typography>
  );
}

function DetailButton({ title, onClick }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 34,
          height: 34,
          justifySelf: 'center',
          bgcolor: 'neutral.50',
          color: 'primary.900',
          boxShadow: '0px 2px 8px rgba(0,0,0,.08)',
          '&:hover': { bgcolor: 'secondary.100' },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function savePdfResponse(response, fileName) {
  const blob = response instanceof Blob
    ? response
    : new Blob([response], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function PaymentHistoryReportPage() {
  const navigate = useNavigate();
  const [reportMode, setReportMode] = useState('consolidated');
  const [employees, setEmployees] = useState([]);
  const [closures, setClosures] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [closureId, setClosureId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [employeeReport, setEmployeeReport] = useState(null);
  const [consolidatedReport, setConsolidatedReport] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const reportRequestId = useRef(0);

  useEffect(() => {
    let active = true;
    Promise.all([listPaymentHistoryEmployees(), listPaymentHistoryClosures()])
      .then(([employeeResponse, closureResponse]) => {
        if (!active) return;
        const employeeOptions = unwrap(employeeResponse);
        const closureOptions = unwrap(closureResponse);
        const employeeList = Array.isArray(employeeOptions) ? employeeOptions : [];
        setEmployees(employeeList);
        setClosures(Array.isArray(closureOptions) ? closureOptions : []);
      })
      .catch((err) => {
        if (active) setError(err?.message || 'No se pudieron cargar los filtros del reporte.');
      })
      .finally(() => {
        if (active) setLoadingOptions(false);
      });
    return () => { active = false; };
  }, []);

  const loadEmployeeReport = useCallback(async () => {
    if (!employeeId) {
      reportRequestId.current += 1;
      setEmployeeReport(null);
      setLoadingReport(false);
      return;
    }
    const requestId = reportRequestId.current + 1;
    reportRequestId.current = requestId;
    setLoadingReport(true);
    setError('');
    try {
      const response = await getEmployeePaymentHistory({
        employeeId,
        closureId,
        dateFrom,
        dateTo,
      });
      if (reportRequestId.current === requestId) {
        setEmployeeReport(unwrap(response));
      }
    } catch (err) {
      if (reportRequestId.current === requestId) {
        setEmployeeReport(null);
        setError(err?.message || 'No se pudo cargar el reporte del empleado.');
      }
    } finally {
      if (reportRequestId.current === requestId) {
        setLoadingReport(false);
      }
    }
  }, [employeeId, closureId, dateFrom, dateTo]);

  const loadConsolidatedReport = useCallback(async () => {
    const requestId = reportRequestId.current + 1;
    reportRequestId.current = requestId;
    setLoadingReport(true);
    setError('');
    try {
      const response = await getConsolidatedPaymentHistory({
        closureId,
        dateFrom,
        dateTo,
      });
      if (reportRequestId.current === requestId) {
        setConsolidatedReport(unwrap(response));
      }
    } catch (err) {
      if (reportRequestId.current === requestId) {
        setConsolidatedReport(null);
        setError(err?.message || 'No se pudo cargar el reporte consolidado.');
      }
    } finally {
      if (reportRequestId.current === requestId) {
        setLoadingReport(false);
      }
    }
  }, [closureId, dateFrom, dateTo]);

  useEffect(() => {
    reportRequestId.current += 1;
    const timer = window.setTimeout(() => {
      if (reportMode === 'employee') {
        loadEmployeeReport();
      } else {
        loadConsolidatedReport();
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadConsolidatedReport, loadEmployeeReport, reportMode]);

  const conceptsForModal = useMemo(
    () => selectedPayment?.balanceConcepts || [],
    [selectedPayment],
  );

  const activeReport = reportMode === 'employee' ? employeeReport : consolidatedReport;

  const handleDownloadPdf = async () => {
    if (!activeReport || (reportMode === 'employee' && !employeeId)) return;
    setDownloading(true);
    setError('');
    try {
      const filters = activeReport.filters || {};
      if (reportMode === 'employee') {
        const response = await downloadEmployeePaymentHistoryPdf({
          employeeId,
          closureId: filters.closureId || '',
          dateFrom: filters.dateFrom || '',
          dateTo: filters.dateTo || '',
        });
        const safeName = String(employeeReport?.employee?.fullName || 'empleado')
          .trim()
          .replace(/[^a-zA-Z0-9_-]+/g, '_');
        savePdfResponse(response, `reporte_pagos_${safeName}.pdf`);
      } else {
        const response = await downloadConsolidatedPaymentHistoryPdf({
          closureId: filters.closureId || '',
          dateFrom: filters.dateFrom || '',
          dateTo: filters.dateTo || '',
        });
        savePdfResponse(response, 'reporte_pagos_consolidado.pdf');
      }
    } catch (err) {
      setError(err?.message || 'No se pudo descargar el PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const handleViewEmployee = (employee) => {
    const filters = consolidatedReport?.filters || {};
    setDateFrom(filters.dateFrom || '');
    setDateTo(filters.dateTo || '');
    setClosureId(filters.closureId ? String(filters.closureId) : '');
    setEmployeeId(String(employee.employeeId));
    setReportMode('employee');
  };

  const handleSelectEmployee = (_event, employee) => {
    if (!employee) {
      if (reportMode === 'consolidated') setEmployeeId('');
      return;
    }

    if (reportMode === 'consolidated') {
      const filters = consolidatedReport?.filters;
      if (filters) {
        setDateFrom(filters.dateFrom || '');
        setDateTo(filters.dateTo || '');
        setClosureId(filters.closureId ? String(filters.closureId) : '');
      }
    }
    setEmployeeId(String(employee.employeeId));
    setReportMode('employee');
  };

  const handleBack = () => {
    if (reportMode === 'employee') {
      setEmployeeId('');
      setEmployeeReport(null);
      setSelectedPayment(null);
      setReportMode('consolidated');
      return;
    }
    navigate('/rrhh/liquidacion');
  };

  const selectedEmployee = employees.find(
    (employee) => String(employee.employeeId) === employeeId,
  ) || null;

  const tableTotals = useMemo(() => {
    if (reportMode === 'employee') {
      if (!employeeReport) return null;
      const paymentCount = Number(employeeReport.summary?.paymentCount || 0);
      const baseAmount = (employeeReport.payments || []).reduce(
        (total, payment) => total + Number(payment.baseAmount || 0),
        0,
      );
      return [
        'TOTALES',
        `${paymentCount} ${paymentCount === 1 ? 'pago' : 'pagos'}`,
        formatMinutes(employeeReport.summary?.workedMinutes),
        '—',
        '—',
        formatMoney(baseAmount),
        formatMoney(employeeReport.summary?.bonusTotal),
        formatMoney(employeeReport.summary?.discountTotal),
        formatMoney(employeeReport.summary?.totalPaid),
        '',
      ];
    }

    if (!consolidatedReport) return null;
    return [
      'TOTALES',
      '',
      consolidatedReport.summary?.paymentCount || 0,
      formatMinutes(consolidatedReport.summary?.workedMinutes),
      formatMoney(consolidatedReport.summary?.baseAmount),
      formatMoney(consolidatedReport.summary?.bonusTotal),
      formatMoney(consolidatedReport.summary?.discountTotal),
      formatMoney(consolidatedReport.summary?.totalPaid),
      '',
    ];
  }, [consolidatedReport, employeeReport, reportMode]);

  return (
    <Stack spacing={1.5} sx={{ p: 2, height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'auto 1fr', md: '1fr auto 1fr' },
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleBack}
          sx={{
            width: 40,
            height: 40,
            justifySelf: 'start',
            bgcolor: 'secondary.500',
            '&:hover': { bgcolor: 'secondary.600' },
          }}
        >
          <ArrowBackIcon sx={{ color: 'white', fontSize: 30 }} />
        </IconButton>
        <Box
          sx={{
            gridColumn: { xs: 2, md: 2 },
            justifySelf: 'center',
            px: 3,
            py: 1.1,
            bgcolor: 'secondary.200',
            borderRadius: '33px',
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.900' }}>
            {reportMode === 'employee'
              ? 'Reporte de pagos por empleado'
              : 'Reporte de pagos'}
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' }, gridColumn: 3 }} />
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <CardShell
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1}
          alignItems={{ lg: 'center' }}
        >
          <Autocomplete
            size="small"
            options={employees}
            value={selectedEmployee}
            onChange={handleSelectEmployee}
            disabled={loadingOptions}
            disableClearable={reportMode === 'employee'}
            getOptionLabel={(employee) => `${employee.fullName} · DNI ${employee.dni}`}
            isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
            noOptionsText="No se encontraron empleados"
            sx={{ minWidth: 220, flex: 1, ...FILTER_FIELD_SX }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={reportMode === 'employee' ? 'Empleado' : 'Buscar empleado'}
                placeholder={reportMode === 'employee' ? '' : 'Nombre o DNI'}
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              />
            )}
          />
          <TextField
            select
            size="small"
            label="Cierre/Pago"
            value={closureId}
            onChange={(event) => setClosureId(event.target.value)}
            disabled={loadingOptions}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 205, flexShrink: 0, ...FILTER_FIELD_SX }}
          >
            <MenuItem value="">Todos los cierres</MenuItem>
            {closures.map((closure) => (
              <MenuItem key={closure.closureId} value={String(closure.closureId)}>
                {closure.name} · {formatPeriod(closure.periodStart, closure.periodEnd)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Desde"
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 145, flexShrink: 0, ...FILTER_FIELD_SX }}
          />
          <TextField
            size="small"
            label="Hasta"
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 145, flexShrink: 0, ...FILTER_FIELD_SX }}
          />
          <Button
            onClick={handleDownloadPdf}
            disabled={!activeReport || loadingReport || downloading}
            startIcon={downloading ? <CircularProgress size={17} color="inherit" /> : <PictureAsPdfIcon />}
            sx={{
              bgcolor: '#ffebee',
              color: '#c62828',
              border: '1px solid #ffcdd2',
              borderRadius: '24px',
              px: 2.3,
              minWidth: 165,
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#ffcdd2' },
            }}
          >
            Descargar PDF
          </Button>
        </Stack>
        {loadingOptions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : reportMode === 'employee' && employees.length === 0 ? (
          <Alert severity="info">No hay empleados con liquidaciones completadas.</Alert>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                bgcolor: 'neutral.50',
                borderRadius: '20px',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  minWidth: reportMode === 'employee' ? 1120 : 1050,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ReportTableHeader
                  columns={reportMode === 'employee' ? EMPLOYEE_GRID_COLS : CONSOLIDATED_GRID_COLS}
                  labels={reportMode === 'employee'
                    ? ['Período', 'Cierre/Pago', 'Horas', 'Modalidad', 'Valor/hora', 'Base', 'Bonos', 'Descuentos', 'Total', 'Detalle']
                    : ['Empleado', 'DNI', 'Pagos', 'Horas', 'Base', 'Bonos', 'Descuentos', 'Total', 'Detalle']}
                />
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  {loadingReport ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                  ) : reportMode === 'employee' ? (
                    !employeeReport?.payments?.length ? (
                      <EmptyReport />
                    ) : employeeReport.payments.map((payment) => (
                      <PaymentRow key={payment.closureEmployeeId} payment={payment} onViewDetails={setSelectedPayment} />
                    ))
                  ) : !consolidatedReport?.employees?.length ? (
                    <EmptyReport />
                  ) : consolidatedReport.employees.map((employee) => (
                    <ConsolidatedRow key={employee.employeeId} employee={employee} onViewEmployee={handleViewEmployee} />
                  ))}
                </Box>
                {!loadingReport && tableTotals && (
                  <ReportTotalsRow
                    columns={reportMode === 'employee' ? EMPLOYEE_GRID_COLS : CONSOLIDATED_GRID_COLS}
                    values={tableTotals}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </CardShell>

      <ClosureEmployeeBalanceModal
        open={Boolean(selectedPayment)}
        onClose={() => setSelectedPayment(null)}
        initialConcepts={conceptsForModal}
        fullName={`${employeeReport?.employee?.fullName || ''} · ${selectedPayment ? formatPeriod(selectedPayment.periodStart, selectedPayment.periodEnd) : ''}`}
        saving={false}
        readOnly
      />
    </Stack>
  );
}

function EmptyReport() {
  return (
    <Typography sx={{ py: 6, textAlign: 'center', fontWeight: 600, color: 'neutral.700' }}>
      No hay pagos para los filtros seleccionados.
    </Typography>
  );
}
